<template>
  <div>
    <v-expansion-panels flat multiline>
      <v-expansion-panel>
        <v-expansion-panel-header :id="`dat-version-panel-${dat.uid}`" dark>
          <v-col class="dat-attribute version-container-info col-3">
            <span :id="`dat-version-${dat.uid}-name`" class="attribute-header">{{ $t(`map.tolldomains.dats.headers.dat_name`) }}<br></span>
            <div class="attribute-header-value">{{ dat.name }}</div>
          </v-col>
          <v-col class="dat-attribute col-1">
            <span :id="`dat-version-${dat.uid}-uid`" class="attribute-header">{{ $t(`map.tolldomains.dats.headers.uid`) }}<br></span>
            <div class="attribute-header-value">{{ dat.uid }}</div>
          </v-col>
          <v-col class="dat-attribute col-2">
            <v-chip
              :id="`dat-version-${dat.uid}-state`"
              dark
              small
              class="mr-9 version-archive-requested"
            >
              {{ $t(`map.tolldomains.dats.states.${getDatStatus(dat).state}`) }}
            </v-chip>
          </v-col>
          <v-col class="dat-attribute col-2">
            <v-tooltip
              v-if="dat.instances && dat.instances.length > 0"
              bottom
            >
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <span :class="`dot-${getDatStatus(dat).flag}`">&nbsp;</span>
                  <span :id="`dat-version-${dat.uid}-usedstate`" class="attribute-header-value">{{ $tc(`map.tolldomains.dats.states.${getDatStatus(dat).flag}`, dat.instances.length) }}</span>
                </div>
              </template>
              <div class="instance-title mt-1 mb-2">{{ $t(`map.tolldomains.dats.list_instances_title`) }}</div>
              <div v-for="instance in dat.instances" :key="instance.datId">
                <span :id="`dat-version-${dat.uid}-usedin-${instance.id}`">{{ instance.name }}</span>
              </div>
            </v-tooltip>
            <div v-else>
              <span :class="`dot-${getDatStatus(dat).flag}`">&nbsp;</span>
              <span :id="`dat-version-${dat.uid}-usedstate`" class="attribute-header-value">{{ $t(`map.tolldomains.dats.states.${getDatStatus(dat).flag}`) }}</span>
            </div>
          </v-col>
          <v-col v-if="dat.validTo" class="dat-attribute valid-to-container">
            <span class="attribute-header">{{ $t(`map.tolldomains.dats.headers.valid_to`) }}<br></span>
            <span :id="`dat-version-${dat.uid}-validto`" class="attribute-header-value">{{ dat.validTo ? formatDateTime(dat.validTo) : '-' }}</span>
          </v-col>
          <v-col v-else class="dat-attribute valid-to-container" />
          <v-col class="dat-attribute action-container col-2">
            <v-icon
              v-if="(getDatStatus(dat).state === 'active' || (getDatStatus(dat).state === 'pending' && dat.datVersions && isCurrent(dat.datVersions[0].id, dat.datVersions[0].validFrom) === 'current'))
                && getDatStatus(dat).flag === 'unused' && getCurrentUserIsAbleToWrite('dat')"
              :id="`dat-version-${dat.uid}-archive`"
              :title="$t('common.archive')"
              dark
              class="mx-2 btn-dat"
              @click.stop="archive()"
            >
              mdi-archive-arrow-down-outline
            </v-icon>
            <div v-else class="mx-2 no-btn-dat" />
            <div v-if="getDatStatus(dat).state !== 'requested'" class="mx-2 btn-dat">
              <EditDatDialog
                v-if="getCurrentUserIsAbleToWrite('dat')"
                :dat="dat"
                :datnames="datnames"
                :masterAccount="masterAccount"
                @editDat="saveDat($event)"
              />
            </div>
            <div v-else class="mx-2 no-btn-dat" />
            <v-icon
              v-if="getCurrentUserIsAbleToWrite('dat') && (getDatStatus(dat).state === 'inactive' || (getDatStatus(dat).state === 'pending' && getDatStatus(dat).flag === 'unused'))"
              :id="`dat-version-${dat.uid}-delete`"
              :title="$t('common.delete')"
              class="mx-2 btn-dat"
              dark
              @click.stop="deleteContainer()"
            >
              mdi-trash-can-outline
            </v-icon>
            <div v-else class="mx-2 no-btn-dat" />
          </v-col>
          <template v-slot:actions>
            <v-icon id="version-expand-button" color="#d1d1d1"> $expand </v-icon>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row justify="end">
            <AddDatVersionDialogStepper
              v-if="getCurrentUserIsAbleToWrite('dat')
                && getDatStatus(dat).state === 'active'
                && dat.datVersions
                && (dat.datVersions.length == 0 || (dat.datVersions.length >= 1 && isCurrent(dat.datVersions[0].id, dat.datVersions[0].validFrom) === 'current'))"
              :datId="dat.id"
              :datType="dat.type.toString().toUpperCase()"
              :masterAccount="masterAccount"
              :datnames="datnames"
              :datVersionIds="getIds()"
              @saveDatVersion="saveDatVersion($event)"
            />
          </v-row>
          <v-row>
            <v-col
              v-for="legend in legends"
              :key="legend.title"
              class="list-legend"
              :cols="legend.cols"
            >
              {{ $t(legend.title) }}
            </v-col>
            <v-col cols="auto" class="btn-delete-version" />
          </v-row>
          <v-row
            v-for="(version, index) in getDatVersions(dat.datVersions)"
            :id="`dat-version-${dat.uid}-row-${index}`"
            :key="version.signature"
            align="center"
            class="dat-versions"
          >
            <v-col :id="`dat-version-${dat.uid}-row-${index}-filename`" cols="4">{{ version.filename }}</v-col>
            <v-col :id="`dat-version-${dat.uid}-row-${index}-device-type`" cols="2">{{ version.deviceType }}</v-col>
            <v-col :id="`dat-version-${dat.uid}-row-${index}-signature`" cols="2">{{ version.signature }}</v-col>
            <v-col
              v-if="!getCurrentUserIsAbleToWrite('dat') || (isCurrent(version.id, version.validFrom) !== 'future') && (getDatStatus(dat).flag === 'used' || getDatStatus(dat).state !== 'inactive')
                || (dat.datVersions && dat.datVersions.length == 1 && getDatStatus(dat).flag === 'used')
              "
              :id="`dat-version-${dat.uid}-row-${index}-validfrom`"
              cols="2"
            >
              {{ formatDateTime(version.validFrom) }}
            </v-col>
            <v-col v-else cols="2">
              <span :id="`dat-version-${dat.uid}-row-${index}-validfrom-edit`" @click="setOpen(true)">
                <DatePicker
                  v-if="index == 0"
                  :id="`dat-version-valid-from-datepicker-${index}`"
                  v-model="validFrom[version.id]"
                  class="dat-version-date-field"
                  :disabledDate="disabledDate"
                  :disabledTime="disabledTime"
                  :placeholder="formatDateTime(version.validFrom)"
                  format="DD/MM/YYYY HH:mm"
                  :showTime="{ format: 'HH:mm' }"
                  :showToday="false"
                  :open="open"
                  @ok="editValidFrom(dat.id, version.id, validFrom[version.id])"
                />
              </span>
            </v-col>
            <v-col cols="2">
              <v-chip
                v-if="isCurrent(version.id, version.validFrom) != 'past'"
                :id="`dat-version-${dat.uid}-row-${index}-status`"
                dark
                small
                :class="isCurrent(version.id, version.validFrom)"
              >
                {{ $t(`map.tolldomains.dats.states.${isCurrent(version.id, version.validFrom)}`) }}
              </v-chip>
            </v-col>
            <v-col cols="auto" class="btn-delete-version">
              <v-icon
                v-if="getCurrentUserIsAbleToWrite('dat') && isCurrent(version.id, version.validFrom) === 'future' && dat.datVersions && dat.datVersions.length > 1"
                :id="`dat-version-${dat.uid}-row-${index}-delete`"
                :title="$t('common.delete')"
                dark
                class="mx-2"
                @click="deleteDatVersion(dat.id, version.id)"
              >
                mdi-trash-can-outline
              </v-icon>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <ConfirmDialog
      :headline="confirmData.headline"
      :text="confirmData.text"
      :plurality="0"
      :actions="confirmData.actions"
      :initialDisplayState="confirmation"
      @modal="confirmation = false"
    />
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import ConfirmDialog from '@/components/common/ConfirmDialog/ConfirmDialog.vue';
import Utils from '@/mixins/Utils';
import { DatRelatedV3, DatVersionV2 } from 'ctp-toll-domain';
import ListLegend from '@/components/common/ListLegend/ListLegend.vue';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import { INotification, store as datsStore } from '@/store/modules/dats/types';
import { store as userPermissionStore } from '@/store/modules/permissions/types';
import moment from 'moment';
import { DatePicker } from 'ant-design-vue';
import { range } from 'lodash';
import AddDatVersionDialogStepper from './AddDatVersionDialogStepper.vue';
import EditDatDialog from './EditDatDialog.vue';

@Component({
  components: {
    DatePicker,
    ListLegend,
    EditDatDialog,
    ConfirmDialog,
    AddDatVersionDialogStepper
  }
})
export default class VersionPanelContainer extends Mixins(Utils) {
    @Prop()
    private masterAccount!: MasterAccountInfo;

    @Prop()
    private dat!: DatRelatedV3;

    @Prop()
    private datnames!: string[];

    @datsStore.Getter
    private getDatStatus!: (dat: DatRelatedV3) => any;

    @datsStore.Action
    private archiveDat!: (dat: DatRelatedV3) => Promise<INotification>;

    @datsStore.Action
    private createDatVersion!: (payload: any) => Promise<INotification>;

    @datsStore.Action
    private editDatVersion!: (payload: any) => Promise<INotification>;

    @datsStore.Action
    private deleteDat!: (dat: DatRelatedV3) => Promise<INotification>;

    @datsStore.Action
    private editDat!: (payload: any) => Promise<INotification>;

    @datsStore.Action
    private deleteVersion!: (payload: any) => Promise<INotification>;

    @userPermissionStore.Getter
    private getCurrentUserIsAbleTo: (readOrWrite: string, featureName: string) => boolean;

    private open = false;

    private legends = [
      { title: 'map.tolldomains.dats.list.header_filename', cols: 4 },
      { title: 'map.tolldomains.dats.list.header_device_type', cols: 2 },
      { title: 'map.tolldomains.dats.list.header_signature', cols: 2 },
      { title: 'map.tolldomains.dats.list.header_valid_from_date', cols: 2 },
      { title: 'map.tolldomains.dats.list.header_version_status', cols: 2 }
    ];

    private confirmation = false;

    private deletedVersion = { datID: 0, versionID: 0 };

    private confirmData = {
      headline: 'common.archive',
      text: 'map.tolldomains.dats.confirm_delete',
      actions: {
        continue: {
          text: 'common.continue', caller: this.handleArchiveDat
        },
        cancel: {
          text: 'common.cancel', caller: this.onActionCancel
        }
      }
    };

    private validFrom: Record<number, unknown> = {};

    private currentDatVersion?: DatVersionV2;

    @Watch('dat', { deep: true })
    private async updateValidFrom() {
      if (this.dat && this.dat.datVersions) {
        this.dat.datVersions.forEach((version: DatVersionV2) => {
          if (!(version.id in this.validFrom)) {
            this.$set(this.validFrom, 'version.id', moment(version.validFrom * 1000));
          }
        });
      }
    }

    private beforeMount() {
      if (this.dat && this.dat.datVersions) {
        this.currentDatVersion = this.dat.datVersions!.find(datVersion => moment.unix(datVersion.validFrom).isBefore(moment(), 'minute'));
      }
    }

    private archive() {
      this.confirmData = {
        headline: 'common.archive',
        text: 'map.tolldomains.dats.confirm_archive',
        actions: {
          continue: {
            text: 'common.confirm', caller: this.handleArchiveDat
          },
          cancel: {
            text: 'common.cancel', caller: this.onActionCancel
          }
        }
      };
      this.confirmation = true;
    }

    private async deleteContainer() {
      this.confirmData = {
        headline: 'common.delete',
        text: 'map.tolldomains.dats.confirm_delete',
        actions: {
          continue: {
            text: 'common.confirm', caller: this.handleDeleteDat
          },
          cancel: {
            text: 'common.cancel', caller: this.onActionCancel
          }
        }
      };
      this.confirmation = true;
    }

    private async deleteDatVersion(datID: number, versionID: number) {
      this.deletedVersion = { datID, versionID };
      this.confirmData = {
        headline: 'common.delete',
        text: 'map.tolldomains.dats.confirm_delete_version',
        actions: {
          continue: {
            text: 'common.confirm', caller: this.handleDeleteDatVersion
          },
          cancel: {
            text: 'common.cancel', caller: this.onActionCancel
          }
        }
      };
      this.confirmation = true;
    }

    private async saveDat(payload: {id: number, name: string}) {
      await this.editDat({ master: this.masterAccount, id: payload.id, body: { name: payload.name } });
      this.$emit('updateList');
    }

    private async saveDatVersion(payload: {datId: number, fileData: any}) {
      await this.createDatVersion(payload);
      this.$emit('updateList');
    }

    private getCurrentUserIsAbleToWrite(featureName: string) {
      return featureName ? this.getCurrentUserIsAbleTo('write', featureName) : false;
    }

    private async handleArchiveDat() {
      await this.archiveDat(this.dat);
      this.$emit('updateList');
      this.confirmation = false;
    }

    private async handleDeleteDat() {
      await this.deleteDat(this.dat);
      this.$emit('updateList');
      this.confirmation = false;
    }

    private async handleDeleteDatVersion() {
      const { datID, versionID } = this.deletedVersion;
      await this.deleteVersion({ datID, versionID });
      this.$emit('updateList');
      this.confirmation = false;
    }

    private onActionCancel() {
      this.confirmation = false;
    }

    private isCurrent(id: number, validFrom: number) {
      if (!validFrom) {
        return 'past';
      }
      if (moment.unix(validFrom).isBefore(moment(), 'minute')) {
        return this.currentDatVersion?.id === id ? 'current' : 'past';
      }
      return 'future';
    }

    getIds() {
      return this.dat.datVersions?.map((dat: DatVersionV2) => dat.id);
    }

    private disabledDate(current: moment.Moment) {
      return current.isBefore(moment().startOf('day'));
    }

    private disabledTime(current: moment.Moment) {
      if (current && current.isBefore(moment().startOf('day').add(1, 'day'))) {
        if (current.hours() > moment().hours()) {
          return {
            disabledHours: () => range(0, moment().hours())
          };
        }
        if (current.minutes() === 59) {
          return {
            disabledHours: () => range(0, moment().hours() + 1)
          };
        }
        return {
          disabledHours: () => range(0, moment().hours()),
          disabledMinutes: () => range(0, moment().minutes() + 1)
        };
      }
      return {};
    }

    private setOpen(open: boolean) {
      this.open = open;
    }

    private editValidFrom(datID: number, versionID: number, validFrom: any) {
      this.editDatVersion({ datID, versionID, body: { validFrom: validFrom.unix() } });
      this.$emit('updateList');
      this.validFrom[versionID] = validFrom;
      this.open = false;
    }

    private getDatVersions(dats: DatVersionV2[]) {
      const datVersionsArray : any = [];
      dats.forEach((datVersion) => {
        datVersionsArray.push({
          id: datVersion.id,
          createdAt: datVersion.createdAt,
          updatedAt: datVersion.updatedAt,
          validFrom: datVersion.validFrom,
          filename: datVersion.datFileDtco.filename,
          signature: datVersion.datFileDtco.signature,
          deviceType: 'DTCO'
        });
        datVersionsArray.push({
          id: datVersion.id,
          createdAt: datVersion.createdAt,
          updatedAt: datVersion.updatedAt,
          validFrom: datVersion.validFrom,
          filename: datVersion.datFileObu.filename,
          signature: datVersion.datFileObu.signature,
          deviceType: 'OBU5+'
        });
      });
      return datVersionsArray;
    }
}
</script>

<style lang="scss" scoped>
@import './styles/version-panel-container.scss';
</style>
